import { Realtime } from 'ably';
import { useCallback, useEffect, useState } from 'react';

import { useAblyAuthUrl } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyAuthUrl';
import type { AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';
import { clusterToEnv } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/utils/clusterToEnv';

// Shared instance
let ablyClient: Realtime | undefined;

export const testOnlyResetAblyClient = () => {
  ablyClient = undefined;
};

export const useAblyClient = (tokenType: AblyTokenType) => {
  // Auth URL will only have a value if the flag is enabled and
  // all the required contexts are available
  const authUrl = useAblyAuthUrl(tokenType);
  const [localAblyClient, setLocalAblyClient] = useState<
    Realtime | undefined
  >();

  const [ablyReady, setAblyReady] = useState(
    ablyClient?.connection.state === 'connected',
  );

  useEffect(() => {
    if (authUrl && !ablyClient) {
      // Using a global variable to store the Ably client for now to ensure we share a single
      // connection across the app even when multiple components using portals are mounted.
      ablyClient = new Realtime({
        authUrl,
        environment: clusterToEnv(window.__HB_ENV__.CLUSTER_NAME),
        logLevel: process.env.NODE_ENV === 'production' ? 1 : 3,
        autoConnect: false, // We will manually connect the client if it's required on the page
      });

      ablyClient.connection.on('connected', () => {
        setAblyReady(true);
      });
      ablyClient.connection.on('disconnected', () => {
        setAblyReady(false);
      });
    }

    if (!authUrl) {
      ablyClient?.close();
      ablyClient = undefined;
    }

    setLocalAblyClient(ablyClient);
  }, [authUrl]);

  // Add connection state listener
  useEffect(() => {
    ablyClient?.connection.on('connected', () => {
      setAblyReady(true);
    });
    ablyClient?.connection.on('disconnected', () => {
      setAblyReady(false);
    });
  }, [authUrl]);

  const ensureConnected = useCallback(() => {
    if (!ablyClient) {
      throw new Error('Ably client could not be initialized.');
    }

    if (ablyClient.connection.state === 'initialized') {
      ablyClient.connect();
    }

    return new Promise<void>((resolve) => {
      if (ablyClient?.connection.state === 'connected') {
        resolve();
      } else {
        ablyClient?.connection.once('connected', () => {
          resolve();
        });
      }
    });
  }, []);

  return { ablyClient: localAblyClient, ablyReady, ensureConnected };
};
